import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { OptionTypeBase } from 'react-select';
import type { StudentType, YourStudentsRecentStateType, YourStudentsStateType } from '../../types';
import type { StudentButtonTextType } from '../../../global/buttons/StudentButton';
import type { StudentsTableAccessors } from '../../../global/tables/StudentsTable';
import type { AllIconType, ElementColorType } from '../../../util/types';
import { your_students_state } from '../../types';
import { CollapseSection, ListItem, ValueHeadline } from '../modules';
import { Alert, Button, Collapse, Loader } from '../../../components';
import { StudentSearch, StudentsTable } from '../../../global';
import { mapGroupReportForLastMonday, mapGroupReportForLastWeeks, mapGroupReportForYourStudents } from '../../../mapper/groupMapper';
import StudentsPassword from '../../../global/span/StudentsPassword';
import { GLOBAL_ICONS } from '../../../util/vars';
import type { SectionProps } from '../Home';
import classNames from 'classnames';

interface Props extends SectionProps {
  sinceDate: string;
}

const recentElements: Record<YourStudentsRecentStateType, {
  color: ElementColorType,
  button: StudentButtonTextType
  columns: StudentsTableAccessors[] | StudentsTableAccessors | null
}> = {
  no_logging: {
    color: 'negative',
    button: 'help',
    columns: 'login_details',
  },
};

type StateType = {
  color: ElementColorType,
  button: StudentButtonTextType
  columns: StudentsTableAccessors[] | StudentsTableAccessors | null
};

const elements: Record<YourStudentsStateType, StateType> = {
  slow_progress: {
    color: 'negative',
    button: 'help',
    columns: null,
  },
  failed_2: {
    color: 'negative',
    button: 'help',
    columns: 'lesson_details',
  },
  struggling: {
    color: 'negative',
    button: 'help',
    columns: null,
  },
  good_usage_low_progressions: {
    color: 'negative',
    button: 'help',
    columns: [ 'minutes', 'progressions' ],
  },
  reassessed: {
    color: 'negative',
    button: 'help',
    columns: 'progress',
  },
};

const StudentInsight = ({ isLoading, groupReport, sinceDate, open, setOpen }: Props): JSX.Element => {
  const { translate } = useI18n();

  const [ data, setData ] = useState<StudentType[]>([]);
  const [ dataRecent, setDataRecent ] = useState<StudentType[]>([]);
  const [ dataLastWeeks, setDataLastWeeks ] = useState<StudentType[]>([]);
  const [ searchBoxValue, setSearchBoxValue ] = useState<OptionTypeBase | null>(null);
  const [ showLoginDetails, setShowLoginDetails ] = useState(false);
  const [ shakeInput, setShakeInput ] = useState(false);

  const searchBox =
    <StudentSearch
      students={data}
      value={searchBoxValue}
      handleChange={(option) => {
        setSearchBoxValue(option);
      }}
      className={`w-full ${classNames({ 'animate-shake': shakeInput })}`}
      data-cy="student-search"
    />;

  const filteredStudent = data.find((student) => student.id === searchBoxValue?.value);
  const filteredStudentRecent = dataRecent.find((student) => student.id === searchBoxValue?.value);
  const filteredStudentLastWeeks = dataLastWeeks.find((student) => student.id === searchBoxValue?.value);
  const filteredStudentStates = filteredStudent?.yourStudentsStates && filteredStudent?.yourStudentsStates.filter((state) => your_students_state.includes(state));
  const filteredStudentNoLogging = (filteredStudentRecent?.yourStudentsStates && filteredStudentRecent?.yourStudentsStates.includes('no_logging'));

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setData(mapGroupReportForYourStudents(groupReport));
    setDataRecent(mapGroupReportForLastMonday(groupReport));
    setDataLastWeeks(mapGroupReportForLastWeeks(groupReport));
  }, [ groupReport ]);

  useEffect(() => {
    setSearchBoxValue(null);
  }, [ data ]);

  useEffect(() => {
    if (searchBoxValue?.value) {
      setOpen('student');
    }
  }, [ searchBoxValue ]);

  useEffect(() => {
    setShakeInput(open && !searchBoxValue);
  }, [ open ]);

  return (
    <CollapseSection
      headline={isLoading ? '' : translate('pages.HOME.STUDENT_INSIGHT.HEADLINE')}
      strapline={isLoading ? '' : translate('pages.HOME.STUDENT_INSIGHT.STRAPLINE')}
      hasContainer={false}
      dataCy={'home_insight'}
      open={open}
      setOpen={setOpen}
      headerComponent={(
        <div className={'container pt-0'}>
          <div className={'w-full lg:w-2/4'}>{searchBox}</div>
        </div>)}
    >
      {isLoading
        ? (<Loader />)
        : (<>
          { filteredStudent && (<>
            <hr />
            <div className={'container'}>
              <Button
                isOutline
                color={'positive'}
                icon={GLOBAL_ICONS[showLoginDetails ? 'see_off' : 'see_on']}
                toggle={showLoginDetails}
                onClick={() => setShowLoginDetails((prev) => !prev)}
              >{translate('components.BUTTONS.VIEW_LOGIN_DETAILS')}</Button>
              <Collapse when={showLoginDetails}>
                <div className={'container mt-md rounded-default border-2 border-header shadow-md'}>
                  <ul className={'space-y-2'}>
                    <li><b>{translate('components.TABLES.LOGIN_DETAILS.USERNAME')}</b> <i>{filteredStudent.loginDetails?.username}</i></li>
                    <li><b>{translate('components.TABLES.LOGIN_DETAILS.PASSWORD')}</b> <i><StudentsPassword fetch={showLoginDetails} studentId={filteredStudent.id}/></i></li>
                  </ul>
                </div>
              </Collapse>
            </div>
            <hr />
            <div className={'container space-y-2'}>
              <SectionTitle>{sinceDate}</SectionTitle>
              <ValueHeadlineWithInfo
                translateKey={'pages.HOME.RECENT.PROGRESSION'}
                value={filteredStudentRecent?.chartsData?.progression}
              />
              <ValueHeadlineWithInfo
                translateKey={'pages.HOME.RECENT.QUALITYLEARNINGTIME'}
                value={filteredStudentRecent?.chartsData?.qualityLearningTime}
              />
              { filteredStudentNoLogging && (
                <div>
                  <StudentInfo
                    data={[ filteredStudent ]}
                    element={recentElements.no_logging}
                    state={'no_logging'}
                    icon={false}
                    title={translate('pages.HOME.STUDENT_INSIGHT.NO_LOGGING')}
                    buttonText={translate('pages.HOME.STUDENT_INSIGHT.HELP', { name: filteredStudent.firstName })}
                  />
                </div> )}
            </div>

            {filteredStudentStates?.length ? (
              <>
                <hr />
                <div className={'container space-y-2'}>
                  <SectionTitle>{translate('pages.HOME.STUDENT_INSIGHT.STUDENT_IS', { name: filteredStudent.fullName })}</SectionTitle>
                  {filteredStudentStates.map((state: YourStudentsStateType) => {
                    return (
                      <div>
                        <StudentInfo
                          key={state}
                          data={[ filteredStudent ]}
                          element={elements[state]}
                          state={state}
                          buttonText={translate('pages.HOME.STUDENT_INSIGHT.HELP', { name: filteredStudent.firstName })}
                        />
                      </div>);
                  })}
                </div>
              </>) : null}

            <hr />
            <div className={'container space-y-2'}>
              <SectionTitle>{translate('pages.HOME.STUDENT_INSIGHT.LAST_WEEKS.HEADLINE')}</SectionTitle>
              <ValueHeadlineWithInfo
                translateKey={'pages.HOME.LAST_WEEKS.PROGRESSION'}
                value={filteredStudentLastWeeks?.chartsData?.progression}
              />
              <ValueHeadlineWithInfo
                translateKey={'pages.HOME.LAST_WEEKS.QUALITYLEARNINGTIME'}
                value={filteredStudentLastWeeks?.chartsData?.qualityLearningTime}
              />
              <ValueHeadlineWithInfo
                translateKey={'pages.HOME.LAST_WEEKS.MATHSAGEIMPROVEMENT'}
                value={filteredStudentLastWeeks?.chartsData?.mathsAgeImprovement}
              />
            </div>
          </>)}
        </>)
      }
    </CollapseSection>
  );
};

const SectionTitle = ( { children } : { children: React.ReactNode }) => (
  <h2 className={'italic underline'}>
    { children }
  </h2>
);

const StudentInfo = ( { data, element, buttonText, state, title, icon }: { data: StudentType[], element: StateType, buttonText: string, state:YourStudentsStateType | YourStudentsRecentStateType, title?: string, icon?: AllIconType | false }) => {
  const { translate } = useI18n();

  return (
    <ListItem
      key={`your_students_state_${state}`}
      students={data}
      description={title ?? translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.DESCRIPTION`, { count: data.length })}
      color={element.color}
      moreInfo={translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.INFO`)}
      buttonText={buttonText}
      icon={icon}
      data-cy={`yourStudents-display-${state}`}
    >
      <StudentsTable
        id={`your_students-list_${state}`}
        data={data}
        column={element.columns}
        color={element.color}
        headerMessage={translate(`pages.HOME.YOUR_STUDENTS.${state.toUpperCase()}.HELP`)}
        state={state}
        data-cy={`yourStudents-table-${state}`}
      />
    </ListItem>
  );
};

const ValueHeadlineWithInfo = ({ translateKey, value } : { translateKey: string, value?: string | number | null })  => {
  const { translate } = useI18n();
  const [ showMoreInfo, setShowMoreInfo ] = useState(false);
  return (
    <>
      <ValueHeadline
        title={translate(`${translateKey}.LABEL`)}
        value={value}
        unit={translate(`${translateKey}.UNIT`, { count: typeof value === 'number' ? value : 0 })}
        showMoreInfo
        highlightValue
        onMoreInfo={() => setShowMoreInfo((state) => !state)}
      />
      <Alert disableIcon show={showMoreInfo} className={'my-md'}>
        <><b>{translate(`${translateKey}.LONG_LABEL`)}</b> - {' '}{translate(`${translateKey}.INFO`)}</>
      </Alert>
    </>
  );
};


StudentInsight.displayName = 'StudentInsight';

export default StudentInsight;
